export const FREE_POSITION_OFFSET_LIMIT_PERCENT = 2000;

export type ResponsiveTransformViewport = 'desktop' | 'tablet' | 'mobile';

export type ResponsiveBlockTransformStyle = {
  offsetX: number;
  offsetY: number;
  offsetXPx: number | null;
  offsetYPx: number | null;
  widthScale: number;
  heightScale: number;
};

export function readResponsiveBlockTransformStyle(
  style: Record<string, unknown>,
  viewport: ResponsiveTransformViewport,
  options?: { isolateViewport?: boolean }
): ResponsiveBlockTransformStyle {
  const source = resolveResponsiveTransformSource(style, viewport, options);
  return {
    offsetX: Number.isFinite(Number(source.offsetX)) ? Number(source.offsetX) : 0,
    offsetY: Number.isFinite(Number(source.offsetY)) ? Number(source.offsetY) : 0,
    offsetXPx: Number.isFinite(Number(source.offsetXPx)) ? Number(source.offsetXPx) : null,
    offsetYPx: Number.isFinite(Number(source.offsetYPx)) ? Number(source.offsetYPx) : null,
    widthScale: Number.isFinite(Number(source.widthScale)) ? Number(source.widthScale) : 1,
    heightScale: Number.isFinite(Number(source.heightScale)) ? Number(source.heightScale) : 1,
  };
}

function resolveResponsiveTransformSource(
  style: Record<string, unknown>,
  viewport: ResponsiveTransformViewport,
  options?: { isolateViewport?: boolean }
): Record<string, unknown> {
  const base =
    style.transform && typeof style.transform === 'object' && !Array.isArray(style.transform)
      ? (style.transform as Record<string, unknown>)
      : {};
  if (viewport === 'desktop') return base;
  const responsiveRoot =
    style.responsiveTransform &&
    typeof style.responsiveTransform === 'object' &&
    !Array.isArray(style.responsiveTransform)
      ? (style.responsiveTransform as Record<string, unknown>)
      : {};
  const slot = responsiveRoot[viewport];
  if (slot && typeof slot === 'object' && !Array.isArray(slot)) {
    return slot as Record<string, unknown>;
  }
  return options?.isolateViewport ? {} : base;
}

function asStyleRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function transformRecordFromStyle(transform: ResponsiveBlockTransformStyle): Record<string, number | null> {
  return {
    offsetX: transform.offsetX,
    offsetY: transform.offsetY,
    offsetXPx: transform.offsetXPx,
    offsetYPx: transform.offsetYPx,
    widthScale: transform.widthScale,
    heightScale: transform.heightScale,
  };
}

export function forkResponsiveTransformsBeforeDesktopStyleEdit(
  style: Record<string, unknown>
): Record<string, unknown> {
  const responsiveRoot =
    style.responsiveTransform &&
    typeof style.responsiveTransform === 'object' &&
    !Array.isArray(style.responsiveTransform)
      ? (style.responsiveTransform as Record<string, unknown>)
      : {};
  const needsTabletFork = !responsiveRoot.tablet;
  const needsMobileFork = !responsiveRoot.mobile;
  if (!needsTabletFork && !needsMobileFork) return style;

  const snapshot = transformRecordFromStyle(readResponsiveBlockTransformStyle(style, 'desktop'));
  return {
    ...style,
    responsiveTransform: {
      ...responsiveRoot,
      ...(needsTabletFork ? { tablet: snapshot } : {}),
      ...(needsMobileFork ? { mobile: snapshot } : {}),
    },
  };
}

export function writeResponsiveBlockTransformStyle(
  style: Record<string, unknown>,
  transform: Record<string, number | null>,
  viewport: ResponsiveTransformViewport
) {
  if (viewport === 'desktop') {
    return {
      ...style,
      transform,
    };
  }
  const responsiveRoot =
    style.responsiveTransform &&
    typeof style.responsiveTransform === 'object' &&
    !Array.isArray(style.responsiveTransform)
      ? (style.responsiveTransform as Record<string, unknown>)
      : {};
  return {
    ...style,
    responsiveTransform: {
      ...responsiveRoot,
      [viewport]: transform,
    },
  };
}

/** Commit a style override from the transform engine into the correct viewport slot. */
export function commitResponsiveBlockStyle(
  currentStyle: Record<string, unknown>,
  styleOverride: Record<string, unknown>,
  viewport: ResponsiveTransformViewport
): Record<string, unknown> {
  const baseStyle =
    viewport === 'desktop'
      ? forkResponsiveTransformsBeforeDesktopStyleEdit(asStyleRecord(currentStyle))
      : asStyleRecord(currentStyle);
  const override = asStyleRecord(styleOverride);

  const resolveTransformForCommit = (): ResponsiveBlockTransformStyle => {
    if (viewport === 'desktop') {
      const mergedRead = { ...baseStyle, ...override };
      return readResponsiveBlockTransformStyle(mergedRead, 'desktop');
    }

    const isolate = { isolateViewport: true };
    const baseSlotTransform = readResponsiveBlockTransformStyle(baseStyle, viewport, isolate);
    const overrideSlotTransform = readResponsiveBlockTransformStyle(override, viewport, isolate);
    const overrideRootTransform = readResponsiveBlockTransformStyle(override, 'desktop');
    const baseRootTransform = readResponsiveBlockTransformStyle(baseStyle, 'desktop');

    const slotChanged =
      JSON.stringify(transformRecordFromStyle(overrideSlotTransform)) !==
      JSON.stringify(transformRecordFromStyle(baseSlotTransform));
    const rootChanged =
      JSON.stringify(transformRecordFromStyle(overrideRootTransform)) !==
      JSON.stringify(transformRecordFromStyle(baseRootTransform));

    if (slotChanged) {
      return overrideSlotTransform;
    }
    if (rootChanged) {
      return overrideRootTransform;
    }
    return baseSlotTransform;
  };

  const transform = resolveTransformForCommit();
  let nextStyle = writeResponsiveBlockTransformStyle(
    baseStyle,
    transformRecordFromStyle(transform),
    viewport
  );
  for (const [key, value] of Object.entries(override)) {
    if (key === 'transform' || key === 'responsiveTransform') continue;
    nextStyle = { ...nextStyle, [key]: value };
  }
  return nextStyle;
}

/** Freeze desktop transform into tablet/mobile slots when they are missing. */
export function ensureResponsiveTransformSlots(
  style: Record<string, unknown> | undefined
): Record<string, unknown> | undefined {
  if (!style || typeof style !== 'object' || Array.isArray(style)) return style;
  const responsiveRoot =
    style.responsiveTransform &&
    typeof style.responsiveTransform === 'object' &&
    !Array.isArray(style.responsiveTransform)
      ? (style.responsiveTransform as Record<string, unknown>)
      : {};
  if (responsiveRoot.tablet && responsiveRoot.mobile) return style;
  const snapshot = transformRecordFromStyle(readResponsiveBlockTransformStyle(style, 'desktop'));
  return {
    ...style,
    responsiveTransform: {
      ...responsiveRoot,
      ...(responsiveRoot.tablet ? {} : { tablet: snapshot }),
      ...(responsiveRoot.mobile ? {} : { mobile: snapshot }),
    },
  };
}

export function clampFreePositionOffset(offsetPx: number, blockSizePx: number) {
  const limit = blockSizePx * (FREE_POSITION_OFFSET_LIMIT_PERCENT / 100);
  return Math.min(limit, Math.max(-limit, offsetPx));
}

export function pixelOffsetToPercent(offsetPx: number, blockSizePx: number) {
  const size = Math.max(1, blockSizePx);
  if (!Number.isFinite(offsetPx)) return 0;
  return (offsetPx / size) * 100;
}
